import axios from "axios";
import { API_CONFIG } from "./apiConfig";

const getBaseURL = () => {
  return API_CONFIG.BUSINESS_BACKEND_URL;
};

const getXAPIKEY = ()=>{
  return API_CONFIG.PAYMEO_KEY
}

const getAuthToken = () => {
  return localStorage.getItem('_t');
};

const getRefreshToken = () => {
  return localStorage.getItem('_rt');
};

const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('_t', token);
  } else {
    localStorage.removeItem('_t');
  }
};

const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem('_rt', token);
  } else {
    localStorage.removeItem('_rt');
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isClient = typeof window !== 'undefined';

const setupInterceptors = (axiosInstance) => {
  const baseURL = getBaseURL();

  axiosInstance.interceptors.request.use(
    (config) => {
      const method = config.method?.toLowerCase();

      // Attach x-api-key header for invoice_generator authentication
      const apiKey = getXAPIKEY();

      if (apiKey) {
        config.headers['x-api-key'] = apiKey;
      }

      if (window.csrfToken && method !== 'get' && method !== 'head') {
        config.headers['X-CSRF-Token'] = window.csrfToken;
      }

      const authToken = getAuthToken();
      if (authToken) {
        config.headers['Authorization'] = `Bearer ${authToken}`;
      }

      const refreshToken = getRefreshToken();
      if (refreshToken && config.url?.includes('refresh-token')) {
        config.headers['x-refresh-token'] = refreshToken;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      if (response.data?.token) {
        setAuthToken(response.data.token);
      }
      if (response.data?.refreshToken) {
        setRefreshToken(response.data.refreshToken);
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) return Promise.reject(error);

      if (originalRequest._retry || originalRequest.url?.includes('refresh-token') || originalRequest.url?.includes('/logout')) {
        return Promise.reject(error);
      }

      // const canRefresh = error.response?.data?.canRefresh === true ||
      //                    (error.response?.status === 401);
        const canRefresh = error.response?.data?.canRefresh === true;

      if (canRefresh && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          isRefreshing = false;
          if (isClient) {
            localStorage.removeItem('_t');
            localStorage.removeItem('userData');
            localStorage.removeItem('accountDetails');
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(`${baseURL}${API_CONFIG.AUTH.REFRESH_TOKEN}`, {
            refreshToken
          }, {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
              'x-refresh-token': refreshToken
            }
          });

          const { token, refreshToken: newRefreshToken } = response.data;

          if (token) {
            setAuthToken(token);
          }
          if (newRefreshToken) {
            setRefreshToken(newRefreshToken);
          }

          processQueue(null, token);

          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axiosInstance(originalRequest);

        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          if (isClient) {
            localStorage.removeItem('_t');
            localStorage.removeItem('_rt');
            localStorage.removeItem('userData');
            localStorage.removeItem('accountDetails');
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
          return Promise.reject(refreshError);
        } finally {
          if (isRefreshing) {
            isRefreshing = false;
          }
        }
      }

      // temporariliy disable 401 handling for external auth expiration until we can implement a better flow
      // if (error.response?.status === 401 && isClient) {
      //   const refreshToken = getRefreshToken();
        
      //   if (!refreshToken) {
      //     localStorage.removeItem('_t');
      //     localStorage.removeItem('userData');
      //     localStorage.removeItem('accountDetails');
      //   }
      // }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

const baseURL = getBaseURL();
export const api = axios.create({
    baseURL: baseURL,
    withCredentials: true,
    timeout: 30000,
});

setupInterceptors(api);

let csrfToken = null;

export const fetchCsrfToken = async () => {
    try {
        const response = await api.get(API_CONFIG.CSRF.TOKEN);
        csrfToken = response.data.csrfToken;
        window.csrfToken = csrfToken;
        return csrfToken;
    } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to fetch CSRF token:', error.message);
        }
        return null;
    }
};

export const initializeCsrfProtection = async () => {
    return await fetchCsrfToken();
};

export const updateAuthToken = (newToken, newRefreshToken = null) => {
    if (newToken) {
        localStorage.setItem('_t', newToken);
    } else {
        localStorage.removeItem('_t');
    }
    if (newRefreshToken) {
        localStorage.setItem('_rt', newRefreshToken);
    } else if (newRefreshToken === null) {
        localStorage.removeItem('_rt');
    }
    window.dispatchEvent(new CustomEvent('auth:token-updated', { detail: { token: newToken } }));
};

export const isAuthenticated = () => {
    return !!getAuthToken();
};

export const getStoredRefreshToken = () => {
    return getRefreshToken();
};

export default api;

export const checkBackendHealth = async () => {
    try {
        const response = await api.get(API_CONFIG.HEALTH.CHECK);
        return true;
    } catch (error) {
        return false;
    }
};
